import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { dictionary } from "@/i18n/translations";
import { api, buildAuthHeaders } from "@/lib/api";
import { setDocumentMeta } from "@/lib/seo";

export default function DashboardSettingsPage({ lang }) {
  const copy = dictionary[lang];
  const { session, user, updateUser } = useAuth();

  const [passwordPayload, setPasswordPayload] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [updating, setUpdating] = useState(false);

  const requiresPasswordChange = useMemo(
    () => Boolean(user?.force_password_change),
    [user?.force_password_change],
  );

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: `${copy.dashboardNav.settings} | ${copy.nav.brand}`,
      description: copy.dashboard.securitySubtitle,
    });
  }, [copy.dashboard.securitySubtitle, copy.dashboardNav.settings, copy.nav.brand, lang]);

  const onSubmit = async (event) => {
    event.preventDefault();

    if (passwordPayload.new_password !== passwordPayload.confirm_password) {
      toast.error(copy.common.passwordMismatch);
      return;
    }

    setUpdating(true);
    try {
      await api.post(
        "/auth/change-password",
        {
          current_password: passwordPayload.current_password,
          new_password: passwordPayload.new_password,
        },
        { headers: buildAuthHeaders(session) },
      );

      toast.success(copy.common.passwordChanged);
      setPasswordPayload({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
      updateUser({ force_password_change: false });
    } catch (error) {
      toast.error(error?.response?.data?.detail || copy.common.requestFailed);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-settings-page">
      <section className="space-y-2" data-testid="dashboard-settings-header">
        <h1 className="text-3xl font-semibold text-stone-900 sm:text-4xl" data-testid="dashboard-settings-title">
          {copy.dashboardNav.settings}
        </h1>
        <p className="text-sm text-stone-600" data-testid="dashboard-settings-subtitle">
          {copy.dashboard.securitySubtitle}
        </p>
      </section>

      <Card className="border-gold-500/20 bg-white/90" data-testid="dashboard-settings-account-card">
        <CardHeader>
          <CardTitle data-testid="dashboard-settings-account-title">{copy.dashboardSettings.accountTitle}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2" data-testid="dashboard-settings-account-content">
          <div>
            <p className="text-xs text-stone-500" data-testid="dashboard-settings-account-email-label">{copy.auth.email}</p>
            <p className="text-sm font-semibold text-stone-900" data-testid="dashboard-settings-account-email-value">{user?.email}</p>
          </div>
          <div>
            <p className="text-xs text-stone-500" data-testid="dashboard-settings-account-role-label">{copy.dashboardSettings.roleLabel}</p>
            <p className="text-sm font-semibold text-stone-900" data-testid="dashboard-settings-account-role-value">
              {user?.role === "admin" ? copy.dashboard.roleAdmin : copy.dashboard.roleStaff}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-gold-500/20 bg-white/90" data-testid="dashboard-settings-password-card">
        <CardHeader>
          <CardTitle data-testid="dashboard-settings-password-title">{copy.dashboard.securityTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          {requiresPasswordChange && (
            <div className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="dashboard-settings-temp-password-alert">
              {copy.dashboard.temporaryPasswordAlert}
            </div>
          )}

          <form className="grid gap-3 md:grid-cols-3" onSubmit={onSubmit} data-testid="dashboard-settings-password-form">
            <div className="space-y-2">
              <label data-testid="dashboard-settings-current-password-label">{copy.dashboard.currentPassword}</label>
              <Input
                type="password"
                minLength={8}
                value={passwordPayload.current_password}
                onChange={(event) =>
                  setPasswordPayload((prev) => ({ ...prev, current_password: event.target.value }))
                }
                required
                data-testid="dashboard-settings-current-password-input"
              />
            </div>

            <div className="space-y-2">
              <label data-testid="dashboard-settings-new-password-label">{copy.dashboard.newPassword}</label>
              <Input
                type="password"
                minLength={8}
                value={passwordPayload.new_password}
                onChange={(event) =>
                  setPasswordPayload((prev) => ({ ...prev, new_password: event.target.value }))
                }
                required
                data-testid="dashboard-settings-new-password-input"
              />
            </div>

            <div className="space-y-2">
              <label data-testid="dashboard-settings-confirm-password-label">{copy.dashboard.confirmPassword}</label>
              <Input
                type="password"
                minLength={8}
                value={passwordPayload.confirm_password}
                onChange={(event) =>
                  setPasswordPayload((prev) => ({ ...prev, confirm_password: event.target.value }))
                }
                required
                data-testid="dashboard-settings-confirm-password-input"
              />
            </div>

            <div className="md:col-span-3" data-testid="dashboard-settings-password-actions">
              <Button type="submit" disabled={updating} data-testid="dashboard-settings-change-password-button">
                {updating ? copy.common.loading : copy.dashboard.changePassword}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
